// goals.js — Goals, Financial Freedom Roadmap, Milestones, Gamification
// Provides: renderGoals, goal CRUD, FI level calc, milestones, insights

import { S, save, stampItem, softDelete } from '../core/state.js';
import { uid, now, f, fU, fT, pc, esc, toUSD, usdToTRY, ds, dividendUSD } from '../core/utils.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm } from '../ui/dialog.js';
import { hVal, hTotUSD, tdAccrued } from '../core/math.js';
import { dividendMonthlyUSD } from '../modules/dividends.js';
import { calcPortfolioXIRR } from '../modules/portfolio.js';
import { reserveTargetUSD, forwardEssentialMonthlyTRY, tryCashPosture, cashTotalUSD } from '../modules/cashflow.js';
import { reserveCoverageUSD } from '../modules/cash.js';
import { netWorthUSD } from '../core/risk-calc.js';
import { markDirty } from '../app/render-loop.js';

// ============ CONSTANTS ============

const FI_LEVELS = [
  { level: 0, name: 'Survival', desc: 'No emergency buffer', color: '#ff5470' },
  { level: 1, name: 'Stability', desc: 'Emergency fund secured', color: '#ff8c42' },
  { level: 2, name: 'Debt-free', desc: 'All liabilities cleared', color: '#e8b84b' },
  { level: 3, name: 'Coast FI', desc: 'Compound growth covers retirement', color: '#7ff0c4' },
  { level: 4, name: 'Barista FI', desc: 'Part-time work covers the gap', color: '#34e2a0' },
  { level: 5, name: 'Lean FI', desc: '25x lean expenses invested', color: '#4DC9F6' },
  { level: 6, name: 'FI', desc: 'Work is optional', color: '#00f0ff' },
  { level: 7, name: 'Fat FI', desc: 'Abundance achieved', color: '#a78bfa' }
];

const NW_MILESTONES = [10000, 25000, 50000, 100000, 250000, 500000, 1000000];

const STREAK_BADGES = [
  { months: 3, name: 'Starter' },
  { months: 6, name: 'Committed' },
  { months: 12, name: 'Dedicated' },
  { months: 24, name: 'Relentless' },
  { months: 36, name: 'Unstoppable' }
];

// ============ FI LEVEL CALCULATOR ============

// Ratio thresholds by FI_LEVELS index (Coast..Fat) — shared with the Details drawer.
const FI_RATIO = { 3: 0.15, 4: 0.5, 5: 0.7, 6: 1.0, 7: 1.3 };

export function calcFILevel() {
  // `|| 1` made annualExp ~40x too small, and fiNumber (= 25x) is the ratio DENOMINATOR
  // for FI levels 3-7. null propagates to a 0 annualExp, which zeroes the ratio rather
  // than reporting a fabricated level. (audit 2026-08-01)
  const fx = S.fxRate > 0 ? S.fxRate : null;
  const coverageUSD = reserveCoverageUSD();          // Hard + Strategic reserve (SSOT)
  const liabUSD = _totalLiabUSD();
  const totalInvested = _totalInvestedUSD();
  // Basis = REAL essential outflow (self-adjusting), NOT the aspirational freedomNumber.
  const annualExp = fx ? forwardEssentialMonthlyTRY() * 12 / fx : 0;
  const fiNumber = annualExp * 25;
  const resTgt = reserveTargetUSD();
  const ratio = fiNumber > 0 ? totalInvested / fiNumber : 0;
  const extra = { coverageUSD, resTgt, liabUSD, totalInvested, annualExp, fiNumber, ratio };

  // Level 1: Stability — reserve COVERAGE (not cash-only) >= target
  if (coverageUSD < resTgt) return { ...FI_LEVELS[0], progress: resTgt > 0 ? coverageUSD / resTgt : 0, ...extra };
  // Debt-clearance stage. FI_LEVELS[2] is the entry NAMED "Debt-free"; this branch used
  // FI_LEVELS[1] ("Stability"), so a user carrying debt was shown the Stability label
  // while the bar beside it measured debt clearance. Dormant today (no open liabilities)
  // but wrong the moment one exists. (audit 2026-08-01)
  //
  // The progress figure stays an asset-coverage ratio rather than payoff progress: with
  // no original-balance baseline recorded per liability there is nothing to measure
  // payoff against, and inventing one would be worse than a stated proxy.
  if (liabUSD > 0) return { ...FI_LEVELS[2], progress: 1 - (liabUSD / (liabUSD + totalInvested)), ...extra };
  if (annualExp <= 0) return { ...FI_LEVELS[2], progress: 1, ...extra };
  // Levels 3-7 by ratio of invested to the FI number.
  // The ring is labelled "% TO NEXT", so it must measure the fraction of the CURRENT band
  // traversed, not the distance from zero to the band's ceiling. The old `ratio / top`
  // form meant that crossing into Coast FI at ratio 0.15 immediately displayed 30%, and
  // every band opened at a non-zero reading. (audit 2026-08-01)
  const band = (lo, hi) => hi > lo ? Math.max(0, Math.min(1, (ratio - lo) / (hi - lo))) : 1;
  if (ratio < 0.15) return { ...FI_LEVELS[2], progress: band(0, 0.15), ...extra };
  if (ratio < 0.5) return { ...FI_LEVELS[3], progress: band(0.15, 0.5), ...extra };
  if (ratio < 0.7) return { ...FI_LEVELS[4], progress: band(0.5, 0.7), ...extra };
  if (ratio < 1.0) return { ...FI_LEVELS[5], progress: band(0.7, 1.0), ...extra };
  if (ratio < 1.3) return { ...FI_LEVELS[6], progress: band(1.0, 1.3), ...extra };
  return { ...FI_LEVELS[7], progress: 1, ...extra };
}

// ============ MILESTONE TRACKER ============

export function checkMilestones(twUSD) {
  if (!twUSD || twUSD <= 0) return;
  NW_MILESTONES.forEach(threshold => {
    if (twUSD >= threshold) {
      const exists = (S.achievements || []).find(a => a.type === 'net_worth' && a.threshold === threshold);
      if (!exists) {
        const ach = {
          id: uid(), type: 'net_worth',
          name: '$' + (threshold >= 1000000 ? (threshold / 1000000) + 'M' : (threshold / 1000) + 'K') + ' Club',
          threshold, unlockedAt: now()
        };
        stampItem(ach);
        S.achievements.push(ach);
        save();
        toast('Milestone unlocked: ' + ach.name, 'a-b');
      }
    }
  });
}

function _milestoneProgress(twUSD) {
  const unlocked = NW_MILESTONES.filter(t => twUSD >= t);
  const nextIdx = unlocked.length;
  const next = NW_MILESTONES[nextIdx] || null;
  const prev = nextIdx > 0 ? NW_MILESTONES[nextIdx - 1] : 0;
  const pct = next ? Math.min(100, ((twUSD - prev) / (next - prev)) * 100) : 100;
  return { unlocked, next, pct };
}

// ============ INVESTMENT STREAK ============

function _calcStreak() {
  const buys = (S.cashTxs || []).filter(t => t.type === 'portfolio_buy');
  if (!buys.length) return 0;

  const months = new Set();
  buys.forEach(b => {
    const d = new Date(b.date);
    months.add(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'));
  });

  let streak = 0;
  const d = new Date();
  for (let i = 0; i < 60; i++) {
    const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    if (months.has(key)) { streak++; } else if (i > 0) break; // allow current month to be missing
    d.setMonth(d.getMonth() - 1);
  }
  return streak;
}

// ============ LIVE COUNTERS & INSIGHTS ============

function _calcInsights() {
  // Days investing
  const since = S.goalSettings?.investingSince || _earliestTxDate();
  const sinceDate = since ? new Date(since) : null;
  const daysInvesting = sinceDate ? Math.max(1, Math.floor((Date.now() - sinceDate) / 86400000)) : 0;

  // Total earned: unrealized portfolio P&L + unrealized trade P&L + realized
  let portPnL = 0;
  S.holdings.forEach(h => {
    const inv = hTotUSD(h);
    const v = hVal(h);
    if (v.vU) portPnL += v.vU - inv;
  });

  let tradePnL = 0;
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    const p = S.prices[t.ticker];
    if (!p) return;
    const pl = (t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * t.leverage;
    tradePnL += toUSD(pl, t.currency || 'USD');
  });

  let realizedPnL = 0;
  S.realized.forEach(r => { realizedPnL += r.pnlUSD || toUSD(r.pnl || 0, r.currency || 'USD'); });

  let divTotal = 0;
  // Employer match EXCLUDED here (statüko decision 2026-07-21): match value already lives
  // inside portPnL as free-share gains — adding the income records too would double-count.
  (S.dividends || []).forEach(d => { if (d.kind === 'match') return; divTotal += dividendUSD(d); });

  // TD accrued interest — same term the dashboard's INV P&L uses (audit W2-3:
  // one canonical definition). TOTAL EARNED ≡ INV P&L + dividends.
  let tdAccruedUSD = 0;
  S.trades.filter(t => t.status !== 'Closed' && t.type === 'TimeDeposit' && t.td).forEach(t => {
    const acc = tdAccrued(t.td);
    if (acc && acc.currentValue) tdAccruedUSD += toUSD(acc.currentValue, t.currency || 'TRY') - toUSD(t.td.principal || 0, t.currency || 'TRY');
  });

  const totalEarned = portPnL + tradePnL + realizedPnL + divTotal + tdAccruedUSD;
  const dailyAvg = daysInvesting > 0 ? totalEarned / daysInvesting : 0;
  const perHour = daysInvesting > 0 ? totalEarned / (daysInvesting * 24) : 0;

  // Monthly passive income (dividends + TD accrued interest)
  // Use rolling 12-month window from dividends.js (excludes DRIP)
  const divMonthly = dividendMonthlyUSD();
  const tdIncome = _calcTDPassiveIncome();
  const monthlyPassive = divMonthly + tdIncome.monthlyEstUSD;

  // Freedom number coverage
  const freedomNumber = S.goalSettings?.freedomNumber || 0;
  const freedomPct = freedomNumber > 0 ? (monthlyPassive / freedomNumber) * 100 : 0;

  // Annual return - XIRR (deposit-timing aware), basis-symmetric cashflows
  // since audit 2026-07-31. Falls back to 0 (hidden) when unavailable, never
  // to the naive totalEarned/currentWealth annualization (audit W2-3).
  let annualReturn = 0;
  if (daysInvesting > 90) {
    try {
      const xr = calcPortfolioXIRR();
      if (xr != null && isFinite(xr)) annualReturn = xr * 100;
    } catch (_e) { /* XIRR unavailable -> hide rather than mislead */ }
  }

  // Projections compound at the real portfolio XIRR; without a reliable rate
  // (young history or XIRR unavailable) they show deposits only - never a
  // synthetic positive rate on a flat or losing book (audit 2026-07-31).
  const monthlyInv = S.base || 0;
  const currentTotal = netWorthUSD();
  const rm = annualReturn !== 0 ? Math.pow(1 + annualReturn / 100, 1 / 12) - 1 : 0;
  const proj = [6, 12, 36, 60].map(m => {
    const g = Math.pow(1 + rm, m);
    const value = rm !== 0 ? currentTotal * g + monthlyInv * ((g - 1) / rm) : currentTotal + monthlyInv * m;
    return { months: m, label: m < 12 ? m + ' months' : (m / 12) + (m === 12 ? ' year' : ' years'), value: Math.round(value) };
  });
  // The hardcoded MSCI World 8% / S&P 500 10% comparison was REMOVED (audit 2026-08-01).
  // annualReturn is a MONEY-weighted XIRR that includes the TRY/USD move; those index
  // figures are TIME-weighted nominal CAGRs. For a Turkish investor converting TRY
  // inflows to USD the currency leg can dominate, so the comparison could report
  // "beating the S&P" during a period that was simply a lira slide. The apples-to-apples
  // answer already exists as calcBenchmarkDCA(): a hypothetical VOO purchase on the SAME
  // contribution schedule, in the same currency frame. It is surfaced on the Dashboard
  // benchmark card, which now also states how many months are actually priced.
  const doubleYears = annualReturn > 0 ? (72 / annualReturn) : 0;

  // Streak
  const streak = _calcStreak();

  return {
    daysInvesting, totalEarned, dailyAvg, perHour, sinceDate,
    monthlyPassive, freedomNumber, freedomPct,
    tdAccruedUSD: tdIncome.totalAccruedUSD, tdMonthlyUSD: tdIncome.monthlyEstUSD,
    proj, doubleYears, annualReturn,
    streak, portPnL, tradePnL, realizedPnL, divTotal, divMonthly
  };
}

// Exported for derived-invariants tests (audit 2026-07-31); pure state math.
export const calcGoalsInsights = _calcInsights;

// ============ CUSTOM GOALS CRUD ============

let editGoalId = null;

export function openGoal(id) {
  editGoalId = id || null;
  const g = id ? S.goals.find(x => x.id === id) : null;

  document.getElementById('goalModTitle').textContent = g ? 'Edit Goal' : 'Add Goal';
  document.getElementById('goalName').value = g ? g.name : '';
  document.getElementById('goalType').value = g ? g.type : 'net_worth';
  document.getElementById('goalCurrency').value = g ? (g.currency || 'USD') : 'USD';
  document.getElementById('goalTarget').value = g ? g.targetAmount : '';
  document.getElementById('goalDelBtn').style.display = g ? '' : 'none';

  toggleGoalLink();
  document.getElementById('moGoal').classList.add('active');
}

export function toggleGoalLink() {
  const type = document.getElementById('goalType').value;
  const wrap = document.getElementById('goalLinkWrap');
  if (type === 'debt_free') {
    wrap.style.display = '';
    const sel = document.getElementById('goalLink');
    sel.innerHTML = (S.liabilities || []).filter(l => l.status === 'open').map(l =>
      `<option value="${l.id}">${esc(l.name)} (${l.currency} ${f(l.remainingAmount)})</option>`
    ).join('') || '<option value="">No open liabilities</option>';
  } else {
    wrap.style.display = 'none';
  }
}

export function saveGoal() {
  const name = document.getElementById('goalName').value.trim();
  const type = document.getElementById('goalType').value;
  const currency = document.getElementById('goalCurrency').value;
  const targetAmount = parseFloat(document.getElementById('goalTarget').value);

  if (!name) return toast('Enter a goal name', 'a-r');
  if (!targetAmount || targetAmount <= 0) return toast('Enter a valid target amount', 'a-r');

  if (editGoalId) {
    const g = S.goals.find(x => x.id === editGoalId);
    if (g) {
      Object.assign(g, { name, type, currency, targetAmount });
      if (type === 'debt_free') g.linkedSource = document.getElementById('goalLink').value;
      stampItem(g);
    }
  } else {
    const g = {
      id: uid(), name, type, currency, targetAmount,
      linkedSource: type === 'debt_free' ? document.getElementById('goalLink').value : null,
      createdAt: now(), completedAt: null, status: 'active'
    };
    stampItem(g);
    S.goals.push(g);
  }

  editGoalId = null;
  save();
  markDirty('goals');
  clMo('moGoal');
  toast(name + ' saved', 'a-b');
}

export async function deleteGoal() {
  if (!editGoalId) return;
  const g = S.goals.find(x => x.id === editGoalId);
  if (!g) return;
  if (!await uiConfirm('Delete goal "' + g.name + '"?', { title: 'Delete goal', okText: 'Delete', danger: true })) return;
  softDelete('goals', editGoalId);
  editGoalId = null;
  save();
  markDirty('goals');
  clMo('moGoal');
  toast('Goal deleted', 'a-y');
}

export function clMoGoal() { editGoalId = null; clMo('moGoal'); }

export function saveGoalSettings() {
  const fn = parseFloat(document.getElementById('gsFreedom').value) || 0;
  const since = document.getElementById('gsSince').value || null;
  S.goalSettings = { freedomNumber: fn, investingSince: since };
  save();
  markDirty('goals', 'dash');
  toast('Goal settings saved', 'a-b');
}

// ============ GOAL PROGRESS CALCULATOR ============

/**
 * The same real return the pace projections compound at, or 0 when there is no reliable
 * one. Kept in one place so an ETA can never quietly use a different rate than the
 * projection sitting next to it. (audit 2026-08-01)
 */
function _lastAnnualReturnPct() {
  try {
    const xr = calcPortfolioXIRR();
    return (xr != null && isFinite(xr)) ? xr * 100 : 0;
  } catch (_e) { return 0; }
}

function _goalProgress(goal) {
  // Set when the figure behind `current` cannot be qualified — the caller must not treat
  // it as achievement, and renderGoals must not latch completedAt on it.
  let uncertain = false;
  let current = 0;
  const target = goal.targetAmount || 1;

  switch (goal.type) {
    // A TRY-denominated goal with no rate has no progress figure. 0 reads as "no progress
 // yet"; a `|| 1` conversion read as 100%+ complete. (audit 2026-08-01)
    case 'net_worth': current = goal.currency === 'TRY' ? (usdToTRY(netWorthUSD()) ?? 0) : netWorthUSD(); break;
    case 'portfolio': {
      let gVal = 0;
      S.holdings.forEach(h => { const v = hVal(h); if (v.vU) gVal += v.vU; });
      current = goal.currency === 'TRY' ? (usdToTRY(gVal) ?? 0) : gVal;
      break;
    }
    case 'cash': {
      // A "₺300,000 buffer" goal read 100% complete on 2026-08-08 while ₺296,030 of the
      // ₺336,800 was this month's bills and the IBKR transfer. A buffer made of money that
      // leaves the account in three weeks is not a buffer. Unreconciled falls back to the raw
      // balance — the same number as before, no silent change of meaning.
      if (goal.currency === 'TRY') {
        const p = tryCashPosture();
        current = p.ok ? p.free : p.current;
        // `ok:false` is the state at the START OF EVERY MONTH, until the new month is opened in
        // Income & Expenses — nothing seeds it on render. Falling back to the whole balance made
        // a ₺300,000 buffer read 112% on the 1st, and `completedAt` is a one-way latch: the goal
        // would sit in the Completed fold permanently on a figure that was ₺40,770 the day before.
        if (!p.ok) uncertain = true;
      } else current = _totalCashUSD();
      break;
    }
    case 'debt_free': {
      const liab = (S.liabilities || []).find(l => l.id === goal.linkedSource);
      if (liab) {
        current = liab.originalAmount - liab.remainingAmount;
      } else {
        const totalLiab = _totalLiabUSD();
        current = totalLiab <= 0 ? target : 0;
      }
      break;
    }
    default: current = 0;
  }

  const pct = Math.min(100, Math.max(0, (current / target) * 100));

  // ETA. The old form was `remaining / S.base` — a savings jar, not a portfolio: it
  // ignored compounding entirely and so always overstated the horizon on a growing book.
  // Solved instead from the same FV-of-annuity model the pace projections use (and which
  // the derived-invariants tests already pin), at the same real return.
  //
  // It still assumes this goal receives the FULL monthly investment. That assumption is
  // deliberate and is now stated in the label rather than left implicit: splitting the
  // budget across goals would need an explicit per-goal share, not an invented rule.
  // (audit 2026-08-01, owner decision #19)
  let eta = null;
  let etaAssumesFullBudget = false;
  const remaining = target - current;
  if (remaining > 0) {
    const monthlyBase = S.base || 0;
    const perMonth = goal.currency === 'USD' ? monthlyBase : usdToTRY(monthlyBase);
    if (perMonth > 0) {
      etaAssumesFullBudget = true;
      const annual = _lastAnnualReturnPct();
      const rm = annual > 0 ? Math.pow(1 + annual / 100, 1 / 12) - 1 : 0;
      if (rm > 0) {
        // FV = P(1+rm)^n + C·((1+rm)^n − 1)/rm  ->  solve for n
        const num = target * rm + perMonth;
        const den = current * rm + perMonth;
        eta = (num > 0 && den > 0) ? Math.ceil(Math.log(num / den) / Math.log(1 + rm)) : null;
      } else {
        eta = Math.ceil(remaining / perMonth);   // no reliable rate: deposits only
      }
      if (!(eta > 0) || !isFinite(eta)) eta = null;
    }
  }

  return { current, target, pct, eta, etaAssumesFullBudget, uncertain, completed: pct >= 100 && !uncertain };
}

// ============ HELPERS ============

// One canonical definition, in cashflow.js. This was the fifth hand-written copy, and it was
// the one summing the last bank reading rather than what the account holds now.
function _totalCashUSD() { return cashTotalUSD(); }

function _totalLiabUSD() {
  return (typeof window.totalLiabilitiesUSD === 'function') ? window.totalLiabilitiesUSD() : 0;
}

function _totalInvestedUSD() {
  let gVal = 0;
  S.holdings.forEach(h => { const v = hVal(h); if (v.vU) gVal += v.vU; });
  let tradeVal = 0;
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    tradeVal += toUSD(t.entry * t.qty, t.currency || 'USD');
    const p = S.prices[t.ticker];
    if (p) tradeVal += toUSD((t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * t.leverage, t.currency || 'USD');
  });
  return gVal + tradeVal + _totalCashUSD();
}

// ⚠ CONTRACT-003 — `_twUSD` WAS A SECOND, DIVERGENT DEFINITION OF NET WORTH.
//
// It summed priced holdings + cash + trade margin + trade P&L − liabilities and stopped there.
// `computeTotalWealthUSD` — the canonical one, the number on the Dashboard — also carries the
// reserve fund, accrued time-deposit interest, and holdings that have no live price (at cost,
// via `hWealthUSD`, rather than dropped for want of a quote).
//
// Measured on the live book 2026-08-16: Dashboard $78,001.59, Goals $71,921.06.
// **$6,080.17 missing, 7.79%** — of which the reserve fund alone is $3,389.18 (the app's own
// `assetParts.reserveFund`) and the remainder is time-deposit accrual plus the holdings term.
// The register carried this as "~4% low"; it was nearly double that.
//
// Every goal on the page ran on the low number: the $100K net-worth goal read 72% instead of
// 78%, and every "months to target" projection started from a base $6K short.
//
// The function is GONE rather than corrected. A corrected copy is still a copy, and this file
// has already lost this argument once — see `_totalCashUSD` twelve lines above, which is a
// one-line delegation for exactly the same reason ("the fifth hand-written copy").
//
// It cannot import `computeTotalWealthUSD` directly: dashboard.js reaches goals.js through
// app/render-loop.js, so that import closes a cycle. `risk-calc.netWorthUSD()` is the accessor
// built for this — main.js injects the canonical provider into it at boot.

function _earliestTxDate() {
  const dates = (S.cashTxs || []).map(t => t.date).filter(Boolean).sort();
  return dates[0] || null;
}

function _calcTDPassiveIncome() {
  // Find ALL open time deposits (daily or monthly)
  const tds = (S.trades || []).filter(t => t.type === 'TimeDeposit' && t.td && t.status === 'Open');
  let totalAccruedUSD = 0;
  let monthlyEstUSD = 0;

  tds.forEach(trade => {
    const tdData = trade.td;
    if (!tdData) return;
    const acc = tdAccrued(tdData);
    if (!acc) return;
    // Deposit currency, not an assumed TRY. A USD deposit was being divided by USD/TRY.
    const cur = trade.currency || 'TRY';
    const accruedLocal = acc.accruedNet || acc.netInt || 0;
    const accruedUSD = toUSD(accruedLocal, cur);
    totalAccruedUSD += accruedUSD;

    // Monthly run-rate. The monthly branch is new: fixed-term deposits used to match
    // NEITHER of the two branches below and contributed exactly 0 to passive income,
    // which understated freedomPct and the FREEDOM GAP. (audit 2026-08-01)
    const tax = tdData.withholdingPct || 0;
    if (acc.monthlyNet != null) {
      monthlyEstUSD += toUSD(acc.monthlyNet, cur);
    } else if (acc.dailyGross) {
      monthlyEstUSD += toUSD(acc.dailyGross * 30 * (1 - tax / 100), cur);
    } else if (acc.daysOpen > 0) {
      monthlyEstUSD += (accruedUSD / acc.daysOpen) * 30;
    }
  });

  return { totalAccruedUSD, monthlyEstUSD };
}

function _fmtK(v) {
  if (Math.abs(v) >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (Math.abs(v) >= 1000) return '$' + Math.round(v / 1000) + 'K';
  return fU(v);
}

// ============ SVG HELPERS ============

const _chk = `<svg width="14" height="14" viewBox="0 0 16 16"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>`;
const _lock = `<svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="2" fill="currentColor" opacity="0.3"/></svg>`;

function _progressRing(pct, size, color) {
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (circ * Math.min(100, pct) / 100);
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="transform:rotate(-90deg)">
    <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="2.5"/>
    <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none" stroke="${color}" stroke-width="2.5" stroke-dasharray="${circ.toFixed(1)}" stroke-dashoffset="${offset.toFixed(1)}" stroke-linecap="round"/>
  </svg>`;
}

// ============ RENDER ============

export function renderGoals() {
  const el = document.getElementById('goalsContent');
  if (!el) return;

  const tw = netWorthUSD();
  const fi = calcFILevel();
  const ins = _calcInsights();
  const ms = _milestoneProgress(tw);
  const streak = ins.streak;

  // Check milestones
  checkMilestones(tw);

  // Auto-complete goals
  S.goals.forEach(g => {
    if (g.status === 'active') {
      const p = _goalProgress(g);
      if (p.completed && !g.completedAt) {
        g.completedAt = now();
        g.status = 'completed';
        stampItem(g);
        save();
        toast('Goal completed: ' + g.name, 'a-b');
      }
    }
  });

  let html = '';

  // ─── HEADER + JOURNEY HERO (Advisory language 2026-07-06 — same numbers, goTicker id preserved for the live ticker) ───
  const fiPct = Math.round((fi.progress || 0) * 100);
  const fiName = FI_LEVELS[fi.level] ? FI_LEVELS[fi.level].name : 'Level ' + (fi.level + 1);
  html += `<div class="mny-hd">
    <span class="mny-live"></span><h1 class="mny-h1" style="background:linear-gradient(90deg,#e8b84b,#34e2a0,#00f0ff);-webkit-background-clip:text;background-clip:text">Goals</h1>
    <span class="mny-meta">FI level <b>${esc(fiName)}</b> · ${fiPct}% to next${streak > 0 ? ` · <b>${streak}mo</b> streak` : ''}</span>
    <button class="mny-add" data-action="openGoal">+ Goal</button>
  </div>
  <div class="mny-lbl">YOUR JOURNEY${ins.sinceDate ? ' · INVESTING SINCE ' + esc(ds(ins.sinceDate.toISOString()).toUpperCase()) : ''}</div>
  <div class="mny-hero">
    <div class="mny-hleft">
      <div class="mny-hlbl">TOTAL EARNED · INV P&amp;L + DIVIDENDS · <span style="color:var(--grn)">● LIVE</span></div>
      <div class="mny-hnum ${pc(ins.totalEarned)}" id="goTicker" style="color:${ins.totalEarned >= 0 ? 'var(--grn)' : 'var(--red)'};text-shadow:0 0 24px ${ins.totalEarned >= 0 ? 'rgba(52,226,160,.3)' : 'rgba(255,84,112,.3)'}">${fU(ins.totalEarned)}</div>
      <div class="mny-chips">
        <span class="mny-chip">investing <b>${ins.daysInvesting}d</b></span>
        <span class="mny-chip">daily avg <b style="color:#e8b84b">${fU(ins.dailyAvg)}</b></span>
        <span class="mny-chip">per hour <b style="color:#e8b84b">${fU(ins.perHour)}</b></span>
        <span class="mny-chip">passive <b style="color:#34e2a0">${fU(ins.monthlyPassive)}/mo</b></span>
      </div>
      <div class="mny-hctx">Ticking while you sleep — dividends + TD interest run at <b>${fU(ins.monthlyPassive)}/month</b> without a single click from you.</div>
    </div>
    <div class="mny-ring">
      <svg width="130" height="130" viewBox="0 0 120 120">
        <circle class="bgc" cx="60" cy="60" r="54"/>
        <circle class="fgc" cx="60" cy="60" r="54" style="stroke:url(#mnyGradR);stroke-dashoffset:${(339 - 339 * fiPct / 100).toFixed(0)}"/>
      </svg>
      <div class="rlbl"><b>${fiPct}%</b><span>TO NEXT FI</span></div>
    </div>
  </div>`;

  // ─── FI ROADMAP (neon, mockup v0.1) ───
  html += `<div class="spk"><div class="panel">
    <h2 class="pt">Financial Freedom Roadmap <small>level ${fi.level} of 7${fi.level < 7 ? ` · ${Math.round(fi.progress * 100)}% to ${esc(FI_LEVELS[fi.level + 1].name)}` : ''}</small></h2>
    <div class="gls-ladder">
      ${FI_LEVELS.map((lvl, i) => {
        const h = 100 - i * 10;
        const cls = i < fi.level ? 'done' : i === fi.level ? 'cur' : '';
        const inner = i < fi.level ? _chk : i === fi.level ? Math.round(fi.progress * 100) + '%' : '';
        return `<div class="gls-lv ${cls}" style="height:${h}%">${inner}</div>`;
      }).join('')}
    </div>
    <div class="gls-lvnames">
      ${FI_LEVELS.map((lvl, i) => `<div class="${i < fi.level ? 'done' : i === fi.level ? 'cur' : ''}">${lvl.name}</div>`).join('')}
    </div>
    <div class="gls-lvcap"><b>Current: ${esc(fi.name)} (Level ${fi.level})</b>${fi.level < 7 ? ' — Next: ' + esc(FI_LEVELS[fi.level + 1].name) + '. ' + esc(FI_LEVELS[fi.level + 1].desc) + '.' : ' — You have achieved financial abundance.'}</div>
    ${(() => {
      const cell = (l, v, cls) => `<div class="m"><div class="l">${l}</div><div class="v ${cls || ''}">${v}</div></div>`;
      let todo = '';
      const rows = FI_LEVELS.map((lvl, i) => {
        const st = i < fi.level ? 'done' : i === fi.level ? 'cur' : 'lock';
        let mets = '', pct = st === 'done' ? _chk : '';
        if (i === 1) {
          mets = cell('coverage', fU(fi.coverageUSD), 'pos') + cell('target', fU(fi.resTgt));
          if (st !== 'done') pct = Math.round((fi.resTgt > 0 ? fi.coverageUSD / fi.resTgt : 0) * 100) + '%';
        } else if (i === 2) {
          mets = cell('liabilities', fU(fi.liabUSD), fi.liabUSD > 0 ? '' : 'pos');
        } else if (i >= 3) {
          const tgt = FI_RATIO[i] * fi.fiNumber, gap = Math.max(0, tgt - fi.totalInvested);
          mets = cell('you have', fU(fi.totalInvested)) + cell('target', fU(tgt)) + cell('gap', fU(gap));
          if (st !== 'done') pct = Math.round((tgt > 0 ? fi.totalInvested / tgt : 0) * 100) + '%';
          if (st === 'cur') todo = `<div class="gls-todo">To advance: deploy <b>~${fU(gap)}</b> more into growth assets (US ETF drift + FI accelerator).</div>`;
        }
        return `<div class="gls-drow ${st}"><div class="nm">${esc(lvl.name)}</div><div class="ds">${esc(lvl.desc)}</div>` +
          `<div class="mets">${mets}</div><div class="pct">${pct}</div></div>` +
          (st === 'cur' ? todo : '');
      }).join('');
      return `<details class="gls-det"><summary><span class="cv">▾</span> Details — where each stage stands</summary>${rows}` +
        `<div class="gls-todo" style="color:var(--t3);padding-top:10px">FI# ${fU(fi.fiNumber)} = 25 × ${fU(fi.annualExp)}/yr real essential outflow (self-adjusting). "You have" = live invested capital.</div></details>`;
    })()}
  </div>`;

  // ─── NET WORTH MILESTONES (neon) ───
  html += `<div class="panel">
    <h2 class="pt">Net Worth Milestones${ms.next ? ` <small>next: ${ms.next >= 1000000 ? '$' + (ms.next / 1000000) + 'M' : '$' + (ms.next / 1000) + 'K'} · ${Math.round(ms.pct)}%</small>` : ''}</h2>
    <div class="gls-miles">
      ${NW_MILESTONES.map(t => {
        const unlocked = tw >= t;
        const isCurrent = !unlocked && ms.next === t;
        const label = t >= 1000000 ? '$' + (t / 1000000) + 'M' : '$' + (t / 1000) + 'K';
        const cls = unlocked ? 'done' : isCurrent ? 'cur' : 'lock';
        const inner = unlocked ? _chk : isCurrent ? Math.round(ms.pct) + '%' : _lock;
        return `<div class="gls-mile ${cls}"><div class="c">${inner}</div><span>${label}</span></div>`;
      }).join('')}
    </div>
  </div>`;

  // ─── CUSTOM GOALS ───
  const activeGoals = S.goals.filter(g => g.status === 'active');
  const completedGoals = S.goals.filter(g => g.status === 'completed');
  html += `<div class="panel">
    <h2 class="pt">My Goals <small>${activeGoals.length} active · ${completedGoals.length} completed</small>
      <button class="glassbtn" data-action="openGoal" style="margin-left:10px">+ Add Goal</button></h2>`;

  if (activeGoals.length === 0 && completedGoals.length === 0) {
    html += `<div class="neo-empty ctr">No goals yet. Set your first financial target!</div>`;
  } else {
    activeGoals.forEach(g => {
      const p = _goalProgress(g);
      const color = p.pct >= 100 ? '#12b87c' : p.pct >= 50 ? '#8b5cf6' : '#ff8c42';
      html += `<div class="gls-grow" data-action="openGoal" data-args='["${g.id}"]'>
        <div class="top"><span class="nm">${esc(g.name)}</span><span class="pctv" style="color:${color}">${Math.round(p.pct)}%</span></div>
        <div class="gls-gbar"><i style="width:${Math.min(100, p.pct)}%;background:${color}"></i></div>
        <div class="sub">
          <span>${g.currency === 'TRY' ? fT(p.current) : fU(p.current)} / ${g.currency === 'TRY' ? fT(p.target) : fU(p.target)}</span>
          <span${p.eta && p.etaAssumesFullBudget ? ' title="Assumes this goal receives your FULL monthly investment, compounding at your current portfolio return. With more than one active goal the real horizon is longer."' : ''}>${p.eta ? '~' + p.eta + ' months' + (p.etaAssumesFullBudget ? '*' : '') : p.completed ? 'Completed' : ''}</span>
        </div>
      </div>`;
    });

    if (completedGoals.length) {
      html += `<details class="jrn-more"><summary>Completed (${completedGoals.length})</summary>`;
      completedGoals.forEach(g => {
        html += `<div class="gls-gdone" data-action="openGoal" data-args='["${g.id}"]'>
          <span class="chk">${_chk}</span> ${esc(g.name)}
          <span style="font-family:var(--mono);font-size:10px;color:var(--t3);margin-left:auto">${g.completedAt ? ds(g.completedAt) : 'Completed'}</span>
        </div>`;
      });
      html += `</details>`;
    }
  }
  html += `</div>`;

  // ─── IF YOU KEEP THIS PACE + PASSIVE INCOME (neon pair) ───
  html += `<div class="gls-grid2"><div class="panel">
    <h2 class="pt">If You Keep This Pace</h2>
    <div class="gls-cells">
      ${ins.proj.map((p, i) => {
        const cls = i === ins.proj.length - 1 ? 'cy' : i >= 2 ? 'gold' : 'pos';
        return `<div class="gls-cell"><div class="l">${esc(String(p.label).toUpperCase())}</div><div class="v ${cls}">${_fmtK(p.value)}</div></div>`;
      }).join('')}
    </div>
  </div>`;

  // ─── DID YOU KNOW ───
  const insights = [];
  // OECD median hourly wage ~$19/hr (2024 PPP-adjusted average across OECD)
  if (ins.totalEarned > 0) {
    const oecdHourlyUSD = 19;
    const oecdEquivHours = Math.round(ins.totalEarned / oecdHourlyUSD);
    insights.push({ icon: 'clock', color: '#34e2a0', text: `Your portfolio earned <b style="color:#34e2a0">${fU(ins.totalEarned)}</b> — equivalent to <b style="color:#e8b84b">${oecdEquivHours.toLocaleString()} hours</b> at OECD median wage` });
  }
  if (ins.annualReturn > 0 && ins.daysInvesting > 90) {
    // States the figure and what it IS, and points at the only comparison in the app that
    // shares its cashflows and currency frame. No index CAGR is quoted here on purpose.
    insights.push({ icon: 'globe', color: '#4DC9F6', text: `Your annual return: <b style="color:#4DC9F6">${f(ins.annualReturn)}%</b> — money-weighted and inclusive of FX. For a like-for-like read, the Dashboard's VOO DCA card runs the same contribution schedule through one index.` });
  }
  if (streak > 0) {
    insights.push({ icon: 'streak', color: '#a78bfa', text: `You've invested <b style="color:#a78bfa">${streak} consecutive month${streak > 1 ? 's' : ''}</b> — consistency beats timing` });
  }
  if (ins.doubleYears > 0 && ins.doubleYears < 50) {
    insights.push({ icon: 'zap', color: '#e8b84b', text: `At this rate, your money will double in <b style="color:#e8b84b">~${ins.doubleYears.toFixed(1)} years</b> (Rule of 72)` });
  }
  if (ins.perHour > 0) {
    insights.push({ icon: 'dollar', color: '#ED93B1', text: `Your money earns <b style="color:#ED93B1">${fU(ins.perHour)}/hour</b> while you sleep — <b style="color:#ED93B1">${fU(ins.dailyAvg)}/day</b>` });
  }

  // Rendered after the streak/achievements pair (mockup v0.1 order) — built here
  // because the insight inputs (ins/streak) are already in scope.
  let knowHtml = '';
  if (insights.length > 0) {
    knowHtml = `<div class="panel">
      <h2 class="pt">Did You Know?</h2>
      <div class="gls-know">
        ${insights.map(i => `<div class="row"><i style="background:${i.color}"></i><span>${i.text}</span></div>`).join('')}
      </div>
    </div>`;
  }

  html += `<div class="panel">
    <h2 class="pt">Passive Income Tracker</h2>
    <div class="gls-cells">
      <div class="gls-cell"><div class="l">MONTHLY PASSIVE</div><div class="v gold">${fU(ins.monthlyPassive)}</div>
        <div class="s">${ins.monthlyPassive === 0 ? 'no sources yet' : [ins.tdMonthlyUSD > 0 ? 'TD ' + fU(ins.tdMonthlyUSD) : '', ins.divMonthly > 0 ? 'div ' + fU(ins.divMonthly) : ''].filter(Boolean).join(' · ')}</div></div>
      <div class="gls-cell"><div class="l">ACCRUED TD</div><div class="v pos">${fU(ins.tdAccruedUSD)}</div><div class="s">open deposits, unrealized</div></div>
      <div class="gls-cell"><div class="l">FREEDOM #</div><div class="v cy">${ins.freedomNumber > 0 ? fU(ins.freedomNumber) + '/mo' : 'Not set'}</div><div class="s">${ins.freedomPct > 0 ? f(ins.freedomPct) + '% covered' : 'set in Goal Settings'}</div></div>
      <div class="gls-cell"><div class="l">FREEDOM GAP</div><div class="v ${ins.freedomPct >= 100 ? 'pos' : 'neg'}">${ins.freedomNumber > 0 ? fU(Math.max(0, ins.freedomNumber - ins.monthlyPassive)) + '/mo' : '—'}</div><div class="s">${ins.freedomPct >= 100 ? 'financially free!' : ins.freedomNumber > 0 ? 'still needed monthly' : ''}</div></div>
    </div>
  </div></div>`;

  // ─── INVESTMENT STREAK + ACHIEVEMENTS (neon pair) ───
  const nextBadge = STREAK_BADGES.find(b => b.months > streak);
  const achs = S.achievements || [];
  html += `<div class="gls-grid2"><div class="panel">
    <h2 class="pt">Investment Streak <small>${nextBadge ? 'next badge: ' + esc(nextBadge.name) + ' (' + nextBadge.months + 'mo)' : 'all badges unlocked'}</small></h2>
    <div style="font-family:var(--mono);font-size:24px;font-weight:700;color:var(--grn)">${streak} month${streak === 1 ? '' : 's'}</div>
    <div class="gls-dots">
      ${Array.from({ length: 12 }, (_, i) => `<i class="${i < streak ? 'on' : ''}"></i>`).join('')}
    </div>
  </div>`;
  if (achs.length > 0) {
    html += `<div class="panel">
      <h2 class="pt">Achievements</h2>
      ${achs.sort((a, b) => (a.threshold || 0) - (b.threshold || 0)).map(a =>
        `<span class="gls-badgechip" title="${a.unlockedAt ? esc(ds(a.unlockedAt)) : ''}">${_chk} ${esc(a.name)}</span>`).join('')}
    </div>`;
  }
  html += `</div>`;

  // ─── DID YOU KNOW (built above, mockup order) ───
  html += knowHtml;

  // ─── GOAL SETTINGS (neon) ───
  const gs = S.goalSettings || {};
  html += `<div class="panel">
    <h2 class="pt">Goal Settings</h2>
    <div class="gls-grid2" style="margin-bottom:0">
      <div class="fgl"><label style="font-size:11px;color:var(--t3);font-family:var(--mono);letter-spacing:.1em">MONTHLY FREEDOM NUMBER (USD)</label>
        <input id="gsFreedom" type="number" step="100" value="${gs.freedomNumber || ''}" placeholder="e.g. 2500" style="width:100%"></div>
      <div class="fgl"><label style="font-size:11px;color:var(--t3);font-family:var(--mono);letter-spacing:.1em">INVESTING SINCE</label>
        <input id="gsSince" type="date" value="${gs.investingSince ? gs.investingSince.slice(0, 10) : ''}" style="width:100%"></div>
    </div>
    <button class="glassbtn" data-action="saveGoalSettings" style="margin-top:12px">Save Settings</button>
  </div></div>`;

  el.innerHTML = html;
  // live "works while you sleep" ticker — total earned ticks up at your real per-second rate
  try { const tk = document.getElementById('goTicker'); if (tk && window.SPK) window.SPK.ticker(tk, ins.totalEarned, ins.dailyAvg / 86400, { prefix: '$', dec: 4, hz: 10 }); } catch (e) { /* non-critical */ }
}

// ============ DASHBOARD SUMMARY ============

export function renderGoalsDashCard() {
  const el = document.getElementById('dashGoals');
  if (!el) return;

  const tw = netWorthUSD();
  const fi = calcFILevel();
  const ms = _milestoneProgress(tw);
  const streak = _calcStreak();

  el.innerHTML = `<div class="card" style="border-color:rgba(83,74,183,0.2)">
    <div class="card-title">Goals</div>
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px">
      <div class="neo-cell flat"><div class="l">FI Level</div><div class="v" style="color:${fi.color};font-size:14px">${fi.name}</div></div>
      <div class="neo-cell flat"><div class="l">Next milestone</div><div class="v" style="font-size:14px">${ms.next ? _fmtK(ms.next) : 'All done!'}</div><div class="s">${ms.next ? Math.round(ms.pct) + '%' : ''}</div></div>
      <div class="neo-cell flat"><div class="l">Streak</div><div class="v" style="color:#a78bfa;font-size:14px">${streak} mo</div></div>
    </div>
  </div>`;
}

// Exported for tests: the completion latch is one-way, so the guard on it needs coverage.
export { _goalProgress as _goalProgressForTest };
